import { logger } from '@getaccept/lib-shared/src/logger';
import Analytics from '@getaccept/lib-shared/src/analytics';
import { LocalStorageKey } from '@getaccept/lib-shared/src/enums/local-storage';
import { capitalizeFirstLetter, validateEmail } from '@getaccept/lib-shared/src/helpers';
import * as LocalStorageHelper from '@getaccept/lib-shared/src/helpers/local-storage.helper';
import type { Language } from '@getaccept/lib-shared/src/languages/enums/language';
import { AnalyticsEvent, AnalyticsUserTrait } from '@getaccept/lib-shared/src/types/analytics';
import { Hubspot } from '../../common/services/hubspot.service';
import type { IdentityProvider } from '../enums/identity-provider';
import type { ErrorMessage } from '../../common/types/error-message';
import type { SignupResponse } from '../types/signup-response';
import { t } from '../../common/helpers/translation';
import * as IdentityProviderHelper from './identity-provider.helper';

interface SignupQueryParams {
  firstName: string;
  lastName: string;
  lang: string;
  entityName: string;
  entityCountryCode: string;
}

export function containsNumbersOrSymbols(strings: string[]) {
  const numberRegex = /\d/;
  const symbolRegex = /[^a-zA-Z0-9]/g;

  return strings.some((string: string) => numberRegex.test(string) || symbolRegex.test(string));
}

export function getNamesAndEntityFromEmail(email: string) {
  const unhandledEmailResponse = {
    firstName: '',
    lastName: '',
    entityName: '',
  };

  if (!validateEmail(email.trim().toLowerCase())) {
    return unhandledEmailResponse;
  }

  const names: string[] = email
    .split('@')[0]
    .split('.')
    .map((name: string) => capitalizeFirstLetter(name));

  if (names.length === 1 || names[0].length === 1 || containsNumbersOrSymbols(names)) {
    return unhandledEmailResponse;
  }

  const lastNames: string[] = names.slice(1, names.length);

  if (lastNames.some((name: string) => name.length === 1)) {
    return unhandledEmailResponse;
  }

  const entity: string = capitalizeFirstLetter(email.split('@')[1].split('.')[0]);

  return {
    firstName: names[0],
    lastName: lastNames.join(' ').trim(),
    entityName: entity.trim(),
  };
}

export function getRedirectUrl({
  registrationUrl,
  queryParams,
}: {
  registrationUrl?: string;
  queryParams: SignupQueryParams;
}) {
  const url = registrationUrl ? `${registrationUrl}&submit=true` : '/login/verify-email';

  const urlParams: URLSearchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(queryParams)) {
    urlParams.append(key as string, value);
  }
  return `${url}?${urlParams.toString()}`;
}

export function setAnalyticsSignup(
  analyticsData: Record<string, unknown>,
  analyticsEvent = AnalyticsEvent.SignupInitiated
) {
  const data = {
    ...analyticsData,
    [AnalyticsUserTrait.SignupVersion]: 1,
  };
  const localData = LocalStorageHelper.getLocalStorage(LocalStorageKey.SignupAnalyticsData);
  const currentSignupData = localData ? JSON.parse(localData) : {};

  LocalStorageHelper.setLocalStorage(
    LocalStorageKey.SignupAnalyticsData,
    JSON.stringify({ ...currentSignupData, ...data })
  );

  Analytics.identify(undefined, data);
  Analytics.track(analyticsEvent, data);
}

export async function setAnalyticsSignupSuccess(
  email?: string,
  registrationUrl?: string,
  emailScore?: number
) {
  try {
    if (email) {
      Hubspot.identify(email);
      await Hubspot.signup(email);
      Analytics.identify(undefined, { [AnalyticsUserTrait.Email]: email });
      LocalStorageHelper.setLocalStorage(LocalStorageKey.VerificationEmail, email);
    }
    if (!registrationUrl) {
      Analytics.track(AnalyticsEvent.SignupBadEmail, {
        [AnalyticsUserTrait.EmailScore]: emailScore,
      });
    }
  } catch (e) {
    logger.error(e);
  }
}

export function setAnalyticsSignupError(errorMessage?: ErrorMessage) {
  Analytics.track(AnalyticsEvent.SignupFailed, {
    [AnalyticsUserTrait.SignupStep]: 1,
    errorMessage: errorMessage?.title ?? t('something-went-wrong'),
  });
  Analytics.identify(undefined, {
    [AnalyticsUserTrait.SignupStep]: 1,
  });
}

function getErrorFromResponse(errorMessage: string, error: ErrorMessage) {
  switch (errorMessage) {
    case 'Could not create user':
      return {
        title: t('could-not-create-user'),
        description: error.description,
      };
    case 'Private or anonymous email is not allowed. Please use your business email':
      return {
        title: t('please-enter-your-business-email'),
        description: t('private-or-anonymous-email-is-not-allowed'),
      };
    case 'Could not validate email':
      return { ...error, title: t('you_have_entered_an_invalid_email-title') };
    default:
      return error;
  }
}

export function getErrorMessage(response?: SignupResponse) {
  const error = {
    title: t('something-went-wrong'),
    description: t('please-try-again-or-contact-support'),
    variables: { link: 'please-try-again-or-contact-support' },
  };

  switch (response?.status) {
    case 0:
      return getErrorFromResponse(response.error ?? '', error);
    case 1:
    case 2:
      return;
    case 3:
    case 4:
      return {
        title:
          response.status === 3 ? t('invalid-formatted-email') : t('email-address-looks-wrong'),
        description: t('did-you-mean_v2'),
        variables: { didYouMean: response.did_you_mean },
      };
    case 5:
      return {
        title: t('you_have_reached_the_max_number_of_signups-title'),
        description: t('you_have_reached_the_max_number_of_signups-description'),
      };
    case 6:
      return {
        title: t('you_have_entered_an_invalid_email-title'),
        description: t('you_have_entered_an_invalid_email-description'),
      };
    default:
      return error;
  }
}

export function launchIdentifier({
  identityProvider,
  templateId,
  registrationCampaignId,
  language,
}: {
  identityProvider: IdentityProvider;
  templateId?: string;
  registrationCampaignId?: string;
  language: Language;
}) {
  const redirUrl = IdentityProviderHelper.createIdentityProviderRedirUrl({
    identityProvider,
    registrationSource: 'signup',
    language,
    registrationCampaign: registrationCampaignId,
    templateId,
  });
  const requestUrl = IdentityProviderHelper.createIdentityProviderReqUrl(
    redirUrl,
    identityProvider
  );

  const popupProps: {
    height: number;
    width: number;
    top: number;
    left: number;
  } = IdentityProviderHelper.centerPopup(600, 600);

  const popupSize = `width=${popupProps.width},height=${popupProps.height},top=${popupProps.top},left=${popupProps.left}`;

  window.open(
    requestUrl,
    'authWindow',
    `toolbar=no,location=no,status=no,menubar=no,scrollbars=yes,resizable=yes,${popupSize}`
  );
}
